import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { ApiError, EventRow, OrganizationRow, VenueRow } from '../api';
import { endpoints, eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, statusTone } from '../ui';
import { INPUT, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * Events (Phase 3). The list, and the first step of setting one up: what it
 * is, where it plays and whose money it is. Everything else — performances,
 * tickets, prices, fees — happens on the event's own page, because those are
 * the parts that get revisited.
 */

const ACCOUNTING_MODES = [
  { value: 'BRE_PROMOTED', label: 'Team promoted', hint: 'Ticket sales and fees are the team’s revenue.' },
  { value: 'RENTAL', label: 'Rental', hint: 'Face value is payable to the renter; fees stay with the team.' },
  { value: 'CO_PROMOTE', label: 'Co-promote', hint: 'Face value is split with a co-promoter (50/50 to start).' },
  { value: 'CUSTOM', label: 'Custom', hint: 'Starts like team promoted; edit the accounting rules afterward.' },
];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export default function EventsPage() {
  const can = usePermissions();
  const [filter, setFilter] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const events = useQuery({ queryKey: ['events', filter], queryFn: () => eventEndpoints.list(filter || undefined) });
  const health = useQuery({ queryKey: ['health'], queryFn: endpoints.health, staleTime: 5 * 60_000 });
  const queryClient = useQueryClient();
  const [seedNote, setSeedNote] = useState<string | null>(null);
  const seed = useMutation({
    mutationFn: () => endpoints.seedEvents(),
    onSuccess: (result) => {
      setSeedNote(result.seeded.length ? `Seeded ${result.seeded.map((e) => `${e.title} (${e.units} units)`).join(', ')}.` : `Nothing to do: ${result.skipped.join(', ')}.`);
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const haunted = useMutation({
    mutationFn: () => endpoints.seedHauntedAuditorium(),
    onSuccess: (result) => {
      setSeedNote(
        result.seeded
          ? `Haunted Auditorium 2026: ${result.seeded.performances.length} nights, ${result.seeded.slots_total} entry times, ${result.seeded.admissions_total} admissions, plus walk-up tickets at the entrance.`
          : `Nothing to do: ${result.skipped ?? 'it already exists'}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const devTools = health.data && health.data.app_environment !== 'production' && can('dev_tools.use');

  if (!can('events.view')) return <NotPermitted permission="events.view" />;

  const rows = events.data?.events ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {seedNote ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{seedNote}</p> : null}
      {creating ? (
        <NewEvent
          onCancel={() => setCreating(false)}
          onError={(cause) => setError(cause.message)}
        />
      ) : null}
      <Panel
        title="Events"
        description={`What is on sale. Each event has its own ${dateWords().many}, ticket types, prices, fees and accounting treatment.`}
        actions={
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className={INPUT} aria-label="Filter by status">
              <option value="">All statuses</option>
              {['DRAFT', 'PUBLISHED', 'CANCELLED', 'ARCHIVED'].map((s) => (
                <option key={s} value={s}>
                  {s.toLowerCase()}
                </option>
              ))}
            </select>
            {devTools ? (
              <Button onClick={() => seed.mutate()} disabled={seed.isPending} title={`Dev only: one on-sale, priced, initialized ${dateWords().one} per seeded venue`}>
                {seed.isPending ? 'Seeding…' : 'Seed sample events'}
              </Button>
            ) : null}
            {devTools ? (
              <Button
                onClick={() => haunted.mutate()}
                disabled={haunted.isPending}
                title="Dev only: Haunted Auditorium 2026 — six nights, entry every 15 minutes from 7:00 to 11:00, ten a time, plus walk-up tickets"
              >
                {haunted.isPending ? 'Building the nights…' : 'Seed Haunted Auditorium'}
              </Button>
            ) : null}
            {can('events.manage') && !creating ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                New event
              </Button>
            ) : null}
          </div>
        }
      >
        <Table headers={['Event', 'Venue', `First ${dateWords().one}`, `${dateWords().Many}`, 'Accounting', 'Status']}>
          {events.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={6}>No events yet.</EmptyRow>
          ) : (
            rows.map((event: EventRow) => (
              <tr key={event.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link to={`/events/${event.id}`} className="font-medium text-accent underline-offset-2 hover:underline">
                    {event.title}
                  </Link>
                  <div className="text-xs text-ink-400">{event.slug}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{event.venue_name}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{event.first_starts_at ? inZone(event.first_starts_at, 'America/Chicago') : '—'}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{event.performance_count ?? 0}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{event.accounting_mode.replaceAll('_', ' ').toLowerCase()}</td>
                <td className="px-5 py-3">
                  <Badge tone={statusTone(event.status)}>{event.status}</Badge>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function NewEvent({ onCancel, onError }: { onCancel: () => void; onError: (error: ApiError) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const venues = useQuery<{ venues: VenueRow[] }, ApiError>({ queryKey: ['venues'], queryFn: endpoints.venues });
  const categories = useQuery({ queryKey: ['event-categories'], queryFn: () => eventEndpoints.categories() });
  const organizations = useQuery({ queryKey: ['organizations'], queryFn: eventEndpoints.organizations });
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [venueId, setVenueId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [mode, setMode] = useState('BRE_PROMOTED');
  const [renterId, setRenterId] = useState('');
  const [promoterId, setPromoterId] = useState('');
  const [newOrg, setNewOrg] = useState('');

  const addOrganization = useMutation({
    mutationFn: (name: string) => eventEndpoints.createOrganization(name),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      if (mode === 'RENTAL') setRenterId(result.organization.id);
      else setPromoterId(result.organization.id);
      setNewOrg('');
    },
    onError,
  });

  const create = useMutation({
    mutationFn: () =>
      eventEndpoints.create({
          title: title.trim(),
          slug: slug.trim() || slugify(title),
          primary_venue_id: venueId,
          category_id: categoryId || null,
          accounting_mode: mode,
          renter_organization_id: mode === 'RENTAL' ? renterId || null : null,
          promoter_organization_id: mode === 'CO_PROMOTE' ? promoterId || null : null,
        }),
    onSuccess: async (result) => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      await navigate(`/events/${result.event.id}`);
    },
    onError,
  });

  const needsOrg = mode === 'RENTAL' ? !renterId : mode === 'CO_PROMOTE' ? !promoterId : false;
  const selectedMode = ACCOUNTING_MODES.find((m) => m.value === mode)!;
  const orgs: OrganizationRow[] = organizations.data?.organizations ?? [];

  return (
    <Panel title="New event" description={`Start with what it is and whose money it is. ${dateWords().Many}, tickets and prices come next, on the event page.`}>
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Title" htmlFor="event-title">
            <input id="event-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Friday Night Comedy" className={`w-72 ${INPUT}`} />
          </Field>
          <Field label="Slug" htmlFor="event-slug" hint={`URL: /events/${slug.trim() || slugify(title) || '…'}`}>
            <input id="event-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(title)} className={`w-56 ${INPUT}`} />
          </Field>
          <Field label="Venue" htmlFor="event-venue">
            <select id="event-venue" value={venueId} onChange={(e) => setVenueId(e.target.value)} className={INPUT}>
              <option value="">— choose —</option>
              {(venues.data?.venues ?? []).map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category" htmlFor="event-category">
            <select id="event-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={INPUT}>
              <option value="">—</option>
              {(categories.data?.categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <Field label="Accounting" htmlFor="event-mode" hint={selectedMode.hint}>
            <select id="event-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={INPUT}>
              {ACCOUNTING_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          {mode === 'RENTAL' || mode === 'CO_PROMOTE' ? (
            <>
              <Field label={mode === 'RENTAL' ? 'Renter' : 'Co-promoter'} htmlFor="event-org">
                <select id="event-org" value={mode === 'RENTAL' ? renterId : promoterId} onChange={(e) => (mode === 'RENTAL' ? setRenterId(e.target.value) : setPromoterId(e.target.value))} className={INPUT}>
                  <option value="">— choose —</option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Or add one" htmlFor="event-new-org">
                <div className="flex gap-2">
                  <input id="event-new-org" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} placeholder="Riverside Comedy LLC" className={`w-56 ${INPUT}`} />
                  <Button type="button" onClick={() => addOrganization.mutate(newOrg.trim())} disabled={!newOrg.trim() || addOrganization.isPending}>
                    Add
                  </Button>
                </div>
              </Field>
            </>
          ) : null}
        </div>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!title.trim() || !venueId || needsOrg || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create draft event'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
